import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import axios from 'axios';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
    ReferenceArea
} from 'recharts';
import { API_URL } from '../../config';
import { tokens } from '../../utils/theme';
import { BtnIcon } from '../../components/SharedPageStyles';
import { copyTableAsJSON } from '../../utils/clipboardUtils';

// --- DATE CONSTANTS & HELPERS ---

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const MONTH_NAMES_SHORT = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const MONTH_TICKS = ['01-01', '02-01', '03-01', '04-01', '05-01', '06-01', '07-01', '08-01', '09-01', '10-01', '11-01', '12-01'];
const MONTH_LABELS = {
    '01-01': 'Jan', '02-01': 'Feb', '03-01': 'Mar', '04-01': 'Apr',
    '05-01': 'May', '06-01': 'Jun', '07-01': 'Jul', '08-01': 'Aug',
    '09-01': 'Sep', '10-01': 'Oct', '11-01': 'Nov', '12-01': 'Dec'
};

function getDaysInMonth(month) {
    const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return days[month - 1];
}

function shiftMonthDay(m, d, delta) {
    const date = new Date(2025, m - 1, d + delta);
    return {
        month: date.getMonth() + 1,
        day: date.getDate()
    };
}

function toDayOfYear(m, d) {
    const daysBefore = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    return daysBefore[m - 1] + d;
}

function isDateInRange(m, d, sm, sd, em, ed) {
    const doy = toDayOfYear(m, d);
    const startDoy = toDayOfYear(sm, sd);
    const endDoy = toDayOfYear(em, ed);
    if (startDoy <= endDoy) {
        return doy >= startDoy && doy <= endDoy;
    } else {
        // Cross-year window (e.g. Nov 15 to Jan 15)
        return doy >= startDoy || doy <= endDoy;
    }
}

function isNearDate(d1Str, d2Str, tolerance = 3) {
    if (!d1Str || !d2Str) return false;
    const [m1, d1] = d1Str.split('-').map(Number);
    const [m2, d2] = d2Str.split('-').map(Number);
    const doy1 = toDayOfYear(m1, d1);
    const doy2 = toDayOfYear(m2, d2);
    const diff = Math.abs(doy1 - doy2);
    return diff <= tolerance || diff >= (365 - tolerance);
}

// --- STYLED COMPONENTS ---

const Container = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.05), rgba(12, 14, 23, 0.95))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.95))'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
    border-radius: 14px;
    padding: 20px 20px 0 20px;
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 20px;
    margin-right: -10px;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
        border-radius: 999px;
        border: 1px solid rgba(128, 203, 196, 0.20);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.4), rgba(128, 203, 196, 0.6));
    }
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-wrap: wrap;
    gap: 12px;
`;

const CategoryTitle = styled.h4`
    font-size: 12px;
    font-weight: 600;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.12em;
`;

const ControlsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

// Seasonax Date Picker Styled Components (Adapted to Trade Terminal Theme)
const DatePickerWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const StepButtonGroup = styled.div`
    display: flex;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(15, 23, 42, 0.04)'};
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)'};
`;

const StepBtn = styled.button`
    background: transparent;
    color: ${props => tokens(props.theme.palette.mode).grey[300]};
    border: none;
    padding: 5px 8px;
    cursor: pointer;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.18s ease;

    &:first-child {
        border-right: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    }

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)'};
        color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
    }
`;

const DateTriggerBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.15)' : 'rgba(15, 118, 110, 0.1)'};
    color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.35)' : 'rgba(15, 118, 110, 0.25)'};
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.18s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.22)' : 'rgba(15, 118, 110, 0.15)'};
        color: ${props => props.theme.palette.mode === 'dark' ? '#ffffff' : tokens(props.theme.palette.mode).primary[700]};
    }
`;

const PresetButtonGroup = styled.div`
    display: inline-flex;
    align-items: center;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(15, 23, 42, 0.04)'};
    border-radius: 6px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)'};
    overflow: hidden;
`;

const PresetBtn = styled.button`
    background: ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.22)' : 'rgba(15, 118, 110, 0.15)')
        : 'transparent'};
    color: ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : tokens(props.theme.palette.mode).grey[300]};
    border: none;
    padding: 5px 10px;
    font-size: 11px;
    cursor: pointer;
    font-weight: ${props => props.$active ? '600' : '500'};
    transition: all 0.15s ease;
    border-right: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};

    &:last-child {
        border-right: none;
    }

    &:hover {
        background: ${props => !props.$active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)')
        : ''};
        color: ${props => props.theme.palette.mode === 'dark' ? '#ffffff' : tokens(props.theme.palette.mode).primary[700]};
    }
`;

const PopoverPresetRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    flex-wrap: wrap;
`;

const PopoverPresetLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.82);
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2500;
    padding: 24px;
    animation: fadeIn 0.15s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ExpandedModalContent = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.06), rgba(12, 14, 23, 0.98))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.98))'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(15, 23, 42, 0.14)'};
    border-radius: 14px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65);
    width: 96vw;
    max-width: 1560px;
    height: 88vh;
    display: flex;
    flex-direction: column;
    padding: 20px 24px;
    position: relative;
`;

const ExpandedModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 14px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    flex-wrap: wrap;
    gap: 12px;
`;

const CloseModalBtn = styled.button`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    color: ${props => tokens(props.theme.palette.mode).grey[300]};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)'};
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.15s ease;

    &:hover {
        background: #ef5350;
        color: #ffffff;
        border-color: #ef5350;
    }
`;

const ExpandedFooterStats = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 12px 16px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(15, 23, 42, 0.04)'};
    border-radius: 8px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    margin-top: 14px;
    flex-wrap: wrap;
    gap: 12px;
`;

const FooterStatItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
`;

const FooterStatLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const FooterStatVal = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
`;

const ChartFooterBar = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 4px;
`;

const PopoverDropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 1000;
    background: ${props => props.theme.palette.mode === 'dark' ? '#141824' : '#ffffff'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[500] : 'rgba(15, 23, 42, 0.14)'};
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
    padding: 16px 18px 12px 18px;
    width: 480px;
    max-width: 95vw;
`;

const CalendarColumns = styled.div`
    display: flex;
    gap: 16px;
`;

const CalendarColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const CalendarDivider = styled.div`
    width: 1px;
    border-right: 1px dashed ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.15)'};
    align-self: stretch;
`;

const MonthNav = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const MonthNavBtn = styled.button`
    background: transparent;
    border: none;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    font-size: 15px;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 4px;
    transition: all 0.15s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
    }
`;

const MonthDropdownWrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const MonthTrigger = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(15, 23, 42, 0.04)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.12)'};
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    min-width: 108px;
    transition: all 0.15s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
        border-color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
    }
`;

const MonthMenu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 1200;
    background: ${props => props.theme.palette.mode === 'dark' ? '#141824' : '#ffffff'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[500] : 'rgba(15, 23, 42, 0.14)'};
    border-radius: 6px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.55);
    padding: 4px 0;
    width: 130px;
    max-height: 220px;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(128, 203, 196, 0.3);
        border-radius: 4px;
    }
`;

const MonthOption = styled.button`
    width: 100%;
    text-align: left;
    padding: 6px 12px;
    font-size: 12px;
    border: none;
    cursor: pointer;
    background: ${props => props.$isSelected
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.18)' : 'rgba(15, 118, 110, 0.12)')
        : 'transparent'};
    color: ${props => props.$isSelected
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
    font-weight: ${props => props.$isSelected ? '600' : '400'};
    transition: all 0.1s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.14)' : 'rgba(15, 118, 110, 0.08)'};
        color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
    }
`;

const CalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    text-align: center;
`;

const DayCell = styled.button`
    height: 28px;
    width: 28px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    border: none;
    cursor: pointer;
    transition: all 0.1s ease;
    font-weight: ${props => props.$isSelected ? '600' : '400'};

    ${props => props.$isSelected && `
        background-color: ${props.theme.palette.mode === 'dark' ? '#80cbc4' : '#0f766e'} !important;
        color: ${props.theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff'} !important;
        border-radius: 4px;
    `}

    ${props => props.$isInRange && !props.$isSelected && `
        background-color: ${props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.15)' : 'rgba(15, 118, 110, 0.1)'};
        color: ${props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
        border-radius: 0;
    `}

    ${props => !props.$isInRange && !props.$isSelected && `
        background-color: transparent;
        color: ${tokens(props.theme.palette.mode).grey[300]};
        border-radius: 4px;
    `}

    &:hover {
        ${props => !props.$isSelected && `
            background-color: ${props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.12)' : 'rgba(15, 118, 110, 0.08)'};
        `}
    }
`;

const ApplyRow = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
`;

const ApplyBtn = styled.button`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.18)' : 'rgba(15, 118, 110, 0.12)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.45)' : 'rgba(15, 118, 110, 0.35)'};
    color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
    border-radius: 6px;
    padding: 6px 32px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.18s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.28)' : 'rgba(15, 118, 110, 0.2)'};
        color: ${props => props.theme.palette.mode === 'dark' ? '#ffffff' : tokens(props.theme.palette.mode).primary[700]};
    }
`;

const ToggleGroup = styled.div`
    display: flex;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(15, 23, 42, 0.04)'};
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)'};
`;

const ToggleBtn = styled.button`
    background: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.15)' : 'rgba(15, 118, 110, 0.1)')
        : 'transparent'};
    color: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : tokens(props.theme.palette.mode).grey[400]};
    border: none;
    padding: 6px 12px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: ${props => props.active ? '600' : '500'};

    &:hover {
        background: ${props => !props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.05)')
        : ''};
    }
`;

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: wrap;
`;

const AssetChips = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
`;

const AssetChip = styled.button`
    background: ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.18)' : 'rgba(15, 118, 110, 0.12)')
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(15, 23, 42, 0.03)')};
    color: ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : tokens(props.theme.palette.mode).grey[400]};
    border: 1px solid ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.45)' : 'rgba(15, 118, 110, 0.35)')
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)')};
    padding: 4px 10px;
    font-size: 11px;
    font-weight: ${props => props.$active ? '600' : '500'};
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${props => !props.$active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.07)' : 'rgba(15, 23, 42, 0.06)')
        : ''};
    }
`;

const MainGrid = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
`;

const StatItem = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(15, 23, 42, 0.02)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.05)'};
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const StatLabel = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${props => tokens(props.theme.palette.mode).grey[300]};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const StatValue = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
`;

const StatSubtext = styled.span`
    font-size: 11px;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const KeyIndicator = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffd700;
    font-size: 11px;
    font-weight: 700;
    margin-left: 4px;
    cursor: help;
    transition: transform 0.15s ease;
    vertical-align: middle;

    &:hover {
        transform: scale(1.25);
    }
`;

const SecondaryGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    border-radius: 6px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    text-align: right;
`;

const Th = styled.th`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(15, 23, 42, 0.04)'};
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    font-weight: 600;
    padding: 8px 12px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 10px;

    &:first-child {
        text-align: left;
    }
`;

const Td = styled.td`
    padding: 7px 12px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.04)'};
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
    font-weight: 500;
    white-space: nowrap;

    &:first-child {
        text-align: left;
        color: ${props => tokens(props.theme.palette.mode).grey[300]};
    }
`;

const CustomTooltipBox = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? '#0c0e17' : '#ffffff'};
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 11px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const CATEGORIES = ["All", "Commodities", "Indices", "Forex", "Crypto"];
const YEARS_OPTIONS = [5, 10, 15];

const SeasonalityTab = () => {
    const theme = useTheme();
    const pickerRef = useRef(null);
    const startMonthDropdownRef = useRef(null);
    const endMonthDropdownRef = useRef(null);

    const [selectedCategory, setSelectedCategory] = useState("All");
    const [selectedAsset, setSelectedAsset] = useState("XAUUSD");
    const [years, setYears] = useState(10);
    const [activeWindow, setActiveWindow] = useState(null); // { start: "01-08", end: "03-24", label: "8 Jan - 24 Mar" }
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Popover state
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [isStartMonthOpen, setIsStartMonthOpen] = useState(false);
    const [isEndMonthOpen, setIsEndMonthOpen] = useState(false);
    const [tempStartMonth, setTempStartMonth] = useState(1);
    const [tempStartDay, setTempStartDay] = useState(8);
    const [tempEndMonth, setTempEndMonth] = useState(3);
    const [tempEndDay, setTempEndDay] = useState(24);
    const [viewStartMonth, setViewStartMonth] = useState(1);
    const [viewEndMonth, setViewEndMonth] = useState(3);
    const [showTradeBreakdown, setShowTradeBreakdown] = useState(false);
    const [showPerformanceCharts, setShowPerformanceCharts] = useState(false);
    const [isChartModalOpen, setIsChartModalOpen] = useState(false);
    const [hoveredCurveDate, setHoveredCurveDate] = useState(null);

    const fetchData = async (asset, yr, customWindow = null) => {
        try {
            setLoading(true);
            setError(null);
            let url = `${API_URL}/intermarket/seasonality?asset=${asset}&years=${yr}`;
            if (customWindow?.start && customWindow?.end) {
                url += `&start_date=${customWindow.start}&end_date=${customWindow.end}`;
            }
            const res = await axios.get(url);
            if (res.data?.error) {
                setError(res.data.error);
            } else {
                setData(res.data);
                if (res.data.activeWindow) {
                    setActiveWindow(res.data.activeWindow);
                }
            }
        } catch (err) {
            console.error("Error fetching seasonality data:", err);
            setError("Failed to load seasonality data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(selectedAsset, years, activeWindow);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAsset, years]);

    // Synchronize popover temp states with activeWindow
    useEffect(() => {
        if (activeWindow?.start && activeWindow?.end) {
            const [sM, sD] = activeWindow.start.split('-').map(Number);
            const [eM, eD] = activeWindow.end.split('-').map(Number);
            setTempStartMonth(sM);
            setTempStartDay(sD);
            setTempEndMonth(eM);
            setTempEndDay(eD);
            setViewStartMonth(sM);
            setViewEndMonth(eM);
        }
    }, [activeWindow]);

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsPickerOpen(false);
                setIsStartMonthOpen(false);
                setIsEndMonthOpen(false);
            } else {
                if (startMonthDropdownRef.current && !startMonthDropdownRef.current.contains(event.target)) {
                    setIsStartMonthOpen(false);
                }
                if (endMonthDropdownRef.current && !endMonthDropdownRef.current.contains(event.target)) {
                    setIsEndMonthOpen(false);
                }
            }
        };
        if (isPickerOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isPickerOpen]);

    // Close modal on Escape key press
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isChartModalOpen) {
                setIsChartModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isChartModalOpen]);

    const handleAssetChange = (ticker) => {
        setSelectedAsset(ticker);
        setActiveWindow(null); // reset to asset default pattern
    };

    // Date range stepper buttons (< and >)
    const handleShiftStart = (delta) => {
        if (!activeWindow) return;
        const [sM, sD] = activeWindow.start.split('-').map(Number);
        const [eM, eD] = activeWindow.end.split('-').map(Number);
        const shifted = shiftMonthDay(sM, sD, delta);
        const sMStr = String(shifted.month).padStart(2, '0');
        const sDStr = String(shifted.day).padStart(2, '0');
        const label = `${shifted.day} ${MONTH_NAMES_SHORT[shifted.month - 1]} - ${eD} ${MONTH_NAMES_SHORT[eM - 1]}`;
        const newWindow = {
            start: `${sMStr}-${sDStr}`,
            end: activeWindow.end,
            label
        };
        setActiveWindow(newWindow);
        fetchData(selectedAsset, years, newWindow);
    };

    const handleShiftEnd = (delta) => {
        if (!activeWindow) return;
        const [sM, sD] = activeWindow.start.split('-').map(Number);
        const [eM, eD] = activeWindow.end.split('-').map(Number);
        const shifted = shiftMonthDay(eM, eD, delta);
        const eMStr = String(shifted.month).padStart(2, '0');
        const eDStr = String(shifted.day).padStart(2, '0');
        const label = `${sD} ${MONTH_NAMES_SHORT[sM - 1]} - ${shifted.day} ${MONTH_NAMES_SHORT[shifted.month - 1]}`;
        const newWindow = {
            start: activeWindow.start,
            end: `${eMStr}-${eDStr}`,
            label
        };
        setActiveWindow(newWindow);
        fetchData(selectedAsset, years, newWindow);
    };

    // Apply button inside popover
    const handleApplyWindow = () => {
        const sM = String(tempStartMonth).padStart(2, '0');
        const sD = String(tempStartDay).padStart(2, '0');
        const eM = String(tempEndMonth).padStart(2, '0');
        const eD = String(tempEndDay).padStart(2, '0');
        const label = `${tempStartDay} ${MONTH_NAMES_SHORT[tempStartMonth - 1]} - ${tempEndDay} ${MONTH_NAMES_SHORT[tempEndMonth - 1]}`;

        const newWindow = {
            start: `${sM}-${sD}`,
            end: `${eM}-${eD}`,
            label
        };
        setActiveWindow(newWindow);
        setIsPickerOpen(false);
        fetchData(selectedAsset, years, newWindow);
    };

    // Current date info for 10-second Pre-Trade Plan checklist
    const todayInfo = useMemo(() => {
        const now = new Date();
        const m = now.getMonth() + 1;
        const d = now.getDate();
        const normD = (m === 2 && d === 29) ? 28 : d;
        const dateKey = `${String(m).padStart(2, '0')}-${String(normD).padStart(2, '0')}`;
        const monthShort = MONTH_NAMES_SHORT[m - 1];
        const monthLong = MONTH_NAMES[m - 1];
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const dayOfWeek = days[now.getDay()];
        return {
            dateKey,
            label: `${d} ${monthShort}`,
            monthShort,
            monthLong,
            dayOfWeek,
            isWeekend: now.getDay() === 0 || now.getDay() === 6
        };
    }, []);

    const currentWeekdayStat = useMemo(() => {
        return (data?.weekdayReturns || []).find(d => d.day === todayInfo.dayOfWeek);
    }, [data?.weekdayReturns, todayInfo.dayOfWeek]);

    const currentMonthStat = useMemo(() => {
        return (data?.monthlyReturns || []).find(m => m.month === todayInfo.monthShort);
    }, [data?.monthlyReturns, todayInfo.monthShort]);

    const isNearToday = useMemo(() => {
        return isNearDate(hoveredCurveDate, todayInfo.dateKey, 3);
    }, [hoveredCurveDate, todayInfo.dateKey]);

    const activePreset = useMemo(() => {
        if (!activeWindow?.start || !activeWindow?.end) return "BEST_PATTERN";
        const now = new Date();
        const currM = now.getMonth() + 1;
        const currD = now.getDate();
        const sMStr = String(currM).padStart(2, '0');
        const daysInM = getDaysInMonth(currM);

        if (activeWindow.start === `${sMStr}-01` && activeWindow.end === `${sMStr}-${String(daysInM).padStart(2, '0')}`) {
            return "THIS_MONTH";
        }

        const shifted = shiftMonthDay(currM, currD, 14);
        const sDStr = String(currD).padStart(2, '0');
        const eMStr = String(shifted.month).padStart(2, '0');
        const eDStr = String(shifted.day).padStart(2, '0');
        if (activeWindow.start === `${sMStr}-${sDStr}` && activeWindow.end === `${eMStr}-${eDStr}`) {
            return "NEXT_14_DAYS";
        }

        const nextM = currM === 12 ? 1 : currM + 1;
        const daysInNextM = getDaysInMonth(nextM);
        const nextMStr = String(nextM).padStart(2, '0');
        if (activeWindow.start === `${nextMStr}-01` && activeWindow.end === `${nextMStr}-${String(daysInNextM).padStart(2, '0')}`) {
            return "NEXT_MONTH";
        }

        if (data?.patterns?.[0] && activeWindow.start === data.patterns[0].start && activeWindow.end === data.patterns[0].end) {
            return "BEST_PATTERN";
        }

        return null;
    }, [activeWindow, data]);

    const handleApplyPreset = (presetType) => {
        const now = new Date();
        const currM = now.getMonth() + 1;
        const currD = now.getDate();

        if (presetType === "THIS_MONTH") {
            const daysInM = getDaysInMonth(currM);
            const sM = String(currM).padStart(2, '0');
            const sD = "01";
            const eM = String(currM).padStart(2, '0');
            const eD = String(daysInM).padStart(2, '0');
            const label = `1 ${MONTH_NAMES_SHORT[currM - 1]} - ${daysInM} ${MONTH_NAMES_SHORT[currM - 1]}`;
            const newWindow = { start: `${sM}-${sD}`, end: `${eM}-${eD}`, label };
            setActiveWindow(newWindow);
            setIsPickerOpen(false);
            fetchData(selectedAsset, years, newWindow);
        } else if (presetType === "NEXT_14_DAYS") {
            const shifted = shiftMonthDay(currM, currD, 14);
            const sM = String(currM).padStart(2, '0');
            const sD = String(currD).padStart(2, '0');
            const eM = String(shifted.month).padStart(2, '0');
            const eD = String(shifted.day).padStart(2, '0');
            const label = `${currD} ${MONTH_NAMES_SHORT[currM - 1]} - ${shifted.day} ${MONTH_NAMES_SHORT[shifted.month - 1]}`;
            const newWindow = { start: `${sM}-${sD}`, end: `${eM}-${eD}`, label };
            setActiveWindow(newWindow);
            setIsPickerOpen(false);
            fetchData(selectedAsset, years, newWindow);
        } else if (presetType === "NEXT_MONTH") {
            const nextM = currM === 12 ? 1 : currM + 1;
            const daysInNextM = getDaysInMonth(nextM);
            const sM = String(nextM).padStart(2, '0');
            const sD = "01";
            const eM = String(nextM).padStart(2, '0');
            const eD = String(daysInNextM).padStart(2, '0');
            const label = `1 ${MONTH_NAMES_SHORT[nextM - 1]} - ${daysInNextM} ${MONTH_NAMES_SHORT[nextM - 1]}`;
            const newWindow = { start: `${sM}-${sD}`, end: `${eM}-${eD}`, label };
            setActiveWindow(newWindow);
            setIsPickerOpen(false);
            fetchData(selectedAsset, years, newWindow);
        } else if (presetType === "BEST_PATTERN") {
            setActiveWindow(null);
            setIsPickerOpen(false);
            fetchData(selectedAsset, years, null);
        }
    };

    // Trigger button label e.g. "8 Jan - 24 Mar"
    const triggerLabel = useMemo(() => {
        if (!activeWindow?.start || !activeWindow?.end) return "Select Dates";
        const [sM, sD] = activeWindow.start.split('-').map(Number);
        const [eM, eD] = activeWindow.end.split('-').map(Number);
        return `${sD} ${MONTH_NAMES_SHORT[sM - 1]} - ${eD} ${MONTH_NAMES_SHORT[eM - 1]}`;
    }, [activeWindow]);

    const handleCopyJSON = () => {
        if (!data) return;
        copyTableAsJSON({
            asset: data.asset,
            activeWindow: data.activeWindow,
            summary: data.summary,
            trades: data.trades
        }, `${data.asset}_Seasonality`);
    };

    // Filter available assets by category
    const filteredAssets = useMemo(() => {
        if (!data?.availableAssets) {
            return [
                { ticker: "XAUUSD", name: "Gold Spot", category: "Commodities" },
                { ticker: "XAGUSD", name: "Silver Spot", category: "Commodities" },
                { ticker: "USOIL", name: "WTI Crude Oil", category: "Commodities" },
                { ticker: "S&P500", name: "S&P 500", category: "Indices" },
                { ticker: "NASDAQ", name: "Nasdaq 100", category: "Indices" },
                { ticker: "RUSSELL 2000", name: "Russell 2000", category: "Indices" },
                { ticker: "USDX", name: "US Dollar Index", category: "Indices" },
                { ticker: "EURUSD", name: "EUR / USD", category: "Forex" },
                { ticker: "USDJPY", name: "USD / JPY", category: "Forex" },
                { ticker: "USDCHF", name: "USD / CHF", category: "Forex" },
                { ticker: "GBPUSD", name: "GBP / USD", category: "Forex" },
                { ticker: "USDCAD", name: "USD / CAD", category: "Forex" },
                { ticker: "AUDUSD", name: "AUD / USD", category: "Forex" },
                { ticker: "BTCUSD", name: "Bitcoin", category: "Crypto" },
                { ticker: "ETHUSD", name: "Ethereum", category: "Crypto" }
            ].filter(a => selectedCategory === "All" || a.category === selectedCategory);
        }
        return data.availableAssets.filter(a => selectedCategory === "All" || a.category === selectedCategory);
    }, [data?.availableAssets, selectedCategory]);

    // Active window boundaries for ReferenceArea
    const isCrossYear = useMemo(() => {
        if (!activeWindow?.start || !activeWindow?.end) return false;
        return activeWindow.start > activeWindow.end;
    }, [activeWindow]);

    const summary = data?.summary || {};

    if (loading && !data) {
        return (
            <Container>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: tokens(theme.palette.mode).grey[400], fontSize: '13px' }}>
                    Loading seasonality data ({selectedAsset})...
                </div>
            </Container>
        );
    }

    if (error && !data) {
        return (
            <Container>
                <div style={{ padding: '20px', color: '#ef5350', background: 'rgba(239, 83, 80, 0.1)', borderRadius: '6px' }}>
                    {error}
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <ScrollableContent>
                {/* Header Row */}
                <HeaderRow>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CategoryTitle>SEASONALITY</CategoryTitle>
                        <span style={{
                            fontSize: '11px',
                            color: tokens(theme.palette.mode).grey[400],
                            fontWeight: 500
                        }}>
                            {data?.name} ({data?.asset})
                        </span>
                    </div>

                    <ControlsRow>
                        <ToggleGroup>
                            {YEARS_OPTIONS.map(yr => (
                                <ToggleBtn
                                    key={yr}
                                    active={years === yr}
                                    onClick={() => setYears(yr)}
                                >
                                    {yr}Y
                                </ToggleBtn>
                            ))}
                        </ToggleGroup>

                        <BtnIcon onClick={handleCopyJSON} title="Copy JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                    </ControlsRow>
                </HeaderRow>

                {/* Category Row with Date Range Picker on the right (en face de CATEGORY, en dessous de 5Y/10Y/15Y) */}
                <FilterRow style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <CategoryTitle style={{ fontSize: '11px', margin: 0 }}>CATEGORY</CategoryTitle>
                        <ToggleGroup>
                            {CATEGORIES.map(cat => (
                                <ToggleBtn
                                    key={cat}
                                    active={selectedCategory === cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    style={{ padding: '4px 10px', fontSize: '11px' }}
                                >
                                    {cat}
                                </ToggleBtn>
                            ))}
                        </ToggleGroup>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {/* Quick Presets Group */}
                        <PresetButtonGroup>
                            <PresetBtn
                                $active={activePreset === "THIS_MONTH"}
                                onClick={() => handleApplyPreset("THIS_MONTH")}
                                title="Set backtest window to current month"
                            >
                                This Month
                            </PresetBtn>
                            <PresetBtn
                                $active={activePreset === "NEXT_14_DAYS"}
                                onClick={() => handleApplyPreset("NEXT_14_DAYS")}
                                title="Set backtest window to next 14 days"
                            >
                                Next 14D
                            </PresetBtn>
                            <PresetBtn
                                $active={activePreset === "BEST_PATTERN"}
                                onClick={() => handleApplyPreset("BEST_PATTERN")}
                                title="Reset to top historical recurring pattern"
                            >
                                Best Pattern
                            </PresetBtn>
                        </PresetButtonGroup>

                        {/* Seasonax Date Window Picker with < > [ 8 Jan - 24 Mar ▾ ] < > */}
                        <DatePickerWrapper ref={pickerRef}>
                            <StepButtonGroup>
                                <StepBtn onClick={() => handleShiftStart(-1)} title="Previous Start Day">
                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"></polyline>
                                    </svg>
                                </StepBtn>
                                <StepBtn onClick={() => handleShiftStart(1)} title="Next Start Day">
                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </StepBtn>
                            </StepButtonGroup>

                            <DateTriggerBtn onClick={() => setIsPickerOpen(!isPickerOpen)}>
                                <span>{triggerLabel}</span>
                                <span style={{ fontSize: '10px' }}>▾</span>
                            </DateTriggerBtn>

                            <StepButtonGroup>
                                <StepBtn onClick={() => handleShiftEnd(-1)} title="Previous End Day">
                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"></polyline>
                                    </svg>
                                </StepBtn>
                                <StepBtn onClick={() => handleShiftEnd(1)} title="Next End Day">
                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </StepBtn>
                            </StepButtonGroup>

                            {isPickerOpen && (
                                <PopoverDropdown>
                                    <PopoverPresetRow>
                                        <PopoverPresetLabel>Quick Horizons:</PopoverPresetLabel>
                                        <PresetButtonGroup>
                                            <PresetBtn
                                                $active={activePreset === "THIS_MONTH"}
                                                onClick={() => handleApplyPreset("THIS_MONTH")}
                                            >
                                                This Month
                                            </PresetBtn>
                                            <PresetBtn
                                                $active={activePreset === "NEXT_14_DAYS"}
                                                onClick={() => handleApplyPreset("NEXT_14_DAYS")}
                                            >
                                                Next 14 Days
                                            </PresetBtn>
                                            <PresetBtn
                                                $active={activePreset === "NEXT_MONTH"}
                                                onClick={() => handleApplyPreset("NEXT_MONTH")}
                                            >
                                                Next Month
                                            </PresetBtn>
                                            <PresetBtn
                                                $active={activePreset === "BEST_PATTERN"}
                                                onClick={() => handleApplyPreset("BEST_PATTERN")}
                                            >
                                                Best Pattern
                                            </PresetBtn>
                                        </PresetButtonGroup>
                                    </PopoverPresetRow>
                                    <CalendarColumns>
                                        {/* Left: Start Date Calendar */}
                                        <CalendarColumn>
                                            <MonthNav>
                                                <MonthNavBtn onClick={() => setViewStartMonth(prev => prev === 1 ? 12 : prev - 1)}>
                                                    ‹
                                                </MonthNavBtn>

                                                <MonthDropdownWrapper ref={startMonthDropdownRef}>
                                                    <MonthTrigger onClick={() => setIsStartMonthOpen(!isStartMonthOpen)}>
                                                        <span>{MONTH_NAMES[viewStartMonth - 1]}</span>
                                                        <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
                                                    </MonthTrigger>
                                                    {isStartMonthOpen && (
                                                        <MonthMenu>
                                                            {MONTH_NAMES.map((name, i) => (
                                                                <MonthOption
                                                                    key={i}
                                                                    $isSelected={viewStartMonth === i + 1}
                                                                    onClick={() => {
                                                                        setViewStartMonth(i + 1);
                                                                        setIsStartMonthOpen(false);
                                                                    }}
                                                                >
                                                                    {name}
                                                                </MonthOption>
                                                            ))}
                                                        </MonthMenu>
                                                    )}
                                                </MonthDropdownWrapper>

                                                <MonthNavBtn onClick={() => setViewStartMonth(prev => prev === 12 ? 1 : prev + 1)}>
                                                    ›
                                                </MonthNavBtn>
                                            </MonthNav>

                                            <CalendarGrid>
                                                {Array.from({ length: getDaysInMonth(viewStartMonth) }, (_, i) => i + 1).map(day => {
                                                    const isSelected = viewStartMonth === tempStartMonth && day === tempStartDay;
                                                    const inRange = isDateInRange(
                                                        viewStartMonth, day,
                                                        tempStartMonth, tempStartDay,
                                                        tempEndMonth, tempEndDay
                                                    );
                                                    return (
                                                        <DayCell
                                                            key={day}
                                                            $isSelected={isSelected}
                                                            $isInRange={inRange}
                                                            onClick={() => {
                                                                setTempStartDay(day);
                                                                setTempStartMonth(viewStartMonth);
                                                            }}
                                                        >
                                                            {day}
                                                        </DayCell>
                                                    );
                                                })}
                                            </CalendarGrid>
                                        </CalendarColumn>

                                        <CalendarDivider />

                                        {/* Right: End Date Calendar */}
                                        <CalendarColumn>
                                            <MonthNav>
                                                <MonthNavBtn onClick={() => setViewEndMonth(prev => prev === 1 ? 12 : prev - 1)}>
                                                    ‹
                                                </MonthNavBtn>

                                                <MonthDropdownWrapper ref={endMonthDropdownRef}>
                                                    <MonthTrigger onClick={() => setIsEndMonthOpen(!isEndMonthOpen)}>
                                                        <span>{MONTH_NAMES[viewEndMonth - 1]}</span>
                                                        <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
                                                    </MonthTrigger>
                                                    {isEndMonthOpen && (
                                                        <MonthMenu>
                                                            {MONTH_NAMES.map((name, i) => (
                                                                <MonthOption
                                                                    key={i}
                                                                    $isSelected={viewEndMonth === i + 1}
                                                                    onClick={() => {
                                                                        setViewEndMonth(i + 1);
                                                                        setIsEndMonthOpen(false);
                                                                    }}
                                                                >
                                                                    {name}
                                                                </MonthOption>
                                                            ))}
                                                        </MonthMenu>
                                                    )}
                                                </MonthDropdownWrapper>

                                                <MonthNavBtn onClick={() => setViewEndMonth(prev => prev === 12 ? 1 : prev + 1)}>
                                                    ›
                                                </MonthNavBtn>
                                            </MonthNav>

                                            <CalendarGrid>
                                                {Array.from({ length: getDaysInMonth(viewEndMonth) }, (_, i) => i + 1).map(day => {
                                                    const isSelected = viewEndMonth === tempEndMonth && day === tempEndDay;
                                                    const inRange = isDateInRange(
                                                        viewEndMonth, day,
                                                        tempStartMonth, tempStartDay,
                                                        tempEndMonth, tempEndDay
                                                    );
                                                    return (
                                                        <DayCell
                                                            key={day}
                                                            $isSelected={isSelected}
                                                            $isInRange={inRange}
                                                            onClick={() => {
                                                                setTempEndDay(day);
                                                                setTempEndMonth(viewEndMonth);
                                                            }}
                                                        >
                                                            {day}
                                                        </DayCell>
                                                    );
                                                })}
                                            </CalendarGrid>
                                        </CalendarColumn>
                                    </CalendarColumns>

                                    <ApplyRow>
                                        <ApplyBtn onClick={handleApplyWindow}>
                                            Apply
                                        </ApplyBtn>
                                    </ApplyRow>
                                </PopoverDropdown>
                            )}
                        </DatePickerWrapper>
                    </div>
                </FilterRow>

                {/* Asset Chips */}
                <FilterRow style={{ marginBottom: '16px' }}>
                    <CategoryTitle style={{ fontSize: '11px', margin: 0 }}>ASSET</CategoryTitle>
                    <AssetChips>
                        {filteredAssets.map(a => (
                            <AssetChip
                                key={a.ticker}
                                $active={selectedAsset === a.ticker}
                                onClick={() => handleAssetChange(a.ticker)}
                            >
                                {a.ticker}
                            </AssetChip>
                        ))}
                    </AssetChips>
                </FilterRow>

                {/* Main Row: Seasonal Curve + Key Statistics Panel */}
                <MainGrid>
                    {/* Left: Composite 365-Day Annual Curve */}
                    <Card>
                        <CardHeader>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CategoryTitle style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                                    ANNUAL SEASONAL TREND (BASE 100)
                                    <KeyIndicator title="Core Signal: Check if current seasonal trend direction aligns with Market Overview medal">★</KeyIndicator>
                                </CategoryTitle>
                                {activeWindow?.label && (
                                    <span style={{
                                        fontSize: '11px',
                                        color: '#80cbc4',
                                        background: 'rgba(128, 203, 196, 0.08)',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(128, 203, 196, 0.25)'
                                    }}>
                                        Window: {activeWindow.label}
                                    </span>
                                )}
                            </div>
                            <span style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400] }}>
                                {years} Years Compounded
                            </span>
                        </CardHeader>

                        <div style={{ width: '100%', height: '340px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={data?.seasonalCurve || []}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                    onMouseMove={(e) => {
                                        if (e && e.activePayload && e.activePayload.length) {
                                            setHoveredCurveDate(e.activePayload[0].payload.date);
                                        }
                                    }}
                                    onMouseLeave={() => setHoveredCurveDate(null)}
                                >
                                    <XAxis
                                        dataKey="date"
                                        ticks={MONTH_TICKS}
                                        tickFormatter={val => MONTH_LABELS[val] || val}
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                        domain={['auto', 'auto']}
                                    />
                                    <ReferenceLine y={100} stroke="rgba(255, 255, 255, 0.15)" strokeDasharray="3 3" />

                                    {/* Today Vertical Marker - label appears only when mouse approaches the line */}
                                    <ReferenceLine
                                        x={todayInfo.dateKey}
                                        stroke={isNearToday ? "#ffd54f" : "rgba(255, 213, 79, 0.45)"}
                                        strokeWidth={isNearToday ? 2.2 : 1.4}
                                        strokeDasharray="3 3"
                                        label={isNearToday ? {
                                            value: `Today (${todayInfo.label})`,
                                            position: 'insideTopRight',
                                            fill: '#ffd54f',
                                            fontSize: 10,
                                            fontWeight: 700
                                        } : undefined}
                                    />

                                    {/* Shaded Pattern Region */}
                                    {activeWindow?.start && activeWindow?.end && (
                                        isCrossYear ? (
                                            <>
                                                <ReferenceArea
                                                    x1={activeWindow.start}
                                                    x2="12-31"
                                                    fill="rgba(128, 203, 196, 0.12)"
                                                    stroke="rgba(128, 203, 196, 0.3)"
                                                    strokeDasharray="2 2"
                                                />
                                                <ReferenceArea
                                                    x1="01-01"
                                                    x2={activeWindow.end}
                                                    fill="rgba(128, 203, 196, 0.12)"
                                                    stroke="rgba(128, 203, 196, 0.3)"
                                                    strokeDasharray="2 2"
                                                />
                                            </>
                                        ) : (
                                            <ReferenceArea
                                                x1={activeWindow.start}
                                                x2={activeWindow.end}
                                                fill="rgba(128, 203, 196, 0.12)"
                                                stroke="rgba(128, 203, 196, 0.3)"
                                                strokeDasharray="2 2"
                                            />
                                        )
                                    )}

                                    <Tooltip
                                        cursor={{ stroke: 'rgba(128, 203, 196, 0.35)', strokeDasharray: '3 3' }}
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const d = payload[0].payload;
                                                const val = d.value;
                                                const diff = (val - 100).toFixed(2);
                                                return (
                                                    <CustomTooltipBox theme={theme}>
                                                        <div style={{ color: tokens(theme.palette.mode).grey[400], marginBottom: 4, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span>{d.label}</span>
                                                            {d.date === todayInfo.dateKey && (
                                                                <span style={{
                                                                    color: '#ffd54f',
                                                                    fontSize: '10px',
                                                                    fontWeight: 700,
                                                                    background: 'rgba(255, 213, 79, 0.15)',
                                                                    padding: '1px 6px',
                                                                    borderRadius: '3px',
                                                                    border: '1px solid rgba(255, 213, 79, 0.4)'
                                                                }}>
                                                                    • Today
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontWeight: 600, color: '#e6edf7' }}>
                                                            Index: {val.toFixed(2)}
                                                        </div>
                                                        <div style={{
                                                            color: val >= 100 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500],
                                                            fontWeight: 500
                                                        }}>
                                                            {diff >= 0 ? `+${diff}%` : `${diff}%`} YTD
                                                        </div>
                                                    </CustomTooltipBox>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#80cbc4"
                                        strokeWidth={1.8}
                                        dot={false}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <ChartFooterBar>
                            <BtnIcon
                                onClick={() => setIsChartModalOpen(true)}
                                title="Plein écran"
                            >
                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 3 21 3 21 9"></polyline>
                                    <polyline points="9 21 3 21 3 15"></polyline>
                                    <line x1="21" y1="3" x2="14" y2="10"></line>
                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                </svg>
                            </BtnIcon>
                        </ChartFooterBar>
                    </Card>

                    {/* Right: Key Performance Summary (Seasonax stats) */}
                    <Card>
                        <CardHeader>
                            <CategoryTitle style={{ margin: 0 }}>STATISTICS</CategoryTitle>
                        </CardHeader>

                        <StatsGrid>
                            <StatItem>
                                <StatLabel>
                                    Winning Trades
                                    <KeyIndicator title="Core Signal: Win rate reliability">★</KeyIndicator>
                                </StatLabel>
                                <StatValue $color={tokens(theme.palette.mode).greenAccent[500]}>
                                    {summary.gainsCount || 0} / {summary.totalTrades || 0}
                                </StatValue>
                                <StatSubtext>{summary.lossesCount || 0} recorded losses</StatSubtext>
                            </StatItem>

                            <StatItem>
                                <StatLabel>Annualized Return</StatLabel>
                                <StatValue $color={(summary.annualizedReturn || 0) >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                    {summary.annualizedReturn != null ? `${summary.annualizedReturn > 0 ? '+' : ''}${summary.annualizedReturn}%` : 'N/A'}
                                </StatValue>
                                <StatSubtext>Annualized return</StatSubtext>
                            </StatItem>

                            <StatItem>
                                <StatLabel>Mean (Avg) Return</StatLabel>
                                <StatValue $color={(summary.avgReturn || 0) >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                    {summary.avgReturn != null ? `${summary.avgReturn > 0 ? '+' : ''}${summary.avgReturn}%` : 'N/A'}
                                </StatValue>
                                <StatSubtext>Average per year</StatSubtext>
                            </StatItem>

                            <StatItem>
                                <StatLabel>Median Return</StatLabel>
                                <StatValue $color={(summary.medianReturn || 0) >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                    {summary.medianReturn != null ? `${summary.medianReturn > 0 ? '+' : ''}${summary.medianReturn}%` : 'N/A'}
                                </StatValue>
                                <StatSubtext>Historical median</StatSubtext>
                            </StatItem>

                            <StatItem>
                                <StatLabel>
                                    Avg / Max Gain
                                    <KeyIndicator title="Core Signal: Risk Asymmetry (Average gain should clearly exceed average loss)">★</KeyIndicator>
                                </StatLabel>
                                <StatValue $color={tokens(theme.palette.mode).greenAccent[500]}>
                                    {summary.avgGain != null ? `+${summary.avgGain}%` : '0%'}
                                </StatValue>
                                <StatSubtext>Max: +{summary.maxGain || 0}%</StatSubtext>
                            </StatItem>

                            <StatItem>
                                <StatLabel>
                                    Avg / Max Loss
                                    <KeyIndicator title="Core Signal: Risk Asymmetry (Compare loss risk against potential gain)">★</KeyIndicator>
                                </StatLabel>
                                <StatValue $color={tokens(theme.palette.mode).redAccent[500]}>
                                    {summary.avgLoss != null ? `${summary.avgLoss}%` : '0%'}
                                </StatValue>
                                <StatSubtext>Max: {summary.maxLoss || 0}%</StatSubtext>
                            </StatItem>

                            <StatItem>
                                <StatLabel>Total Profit Pts</StatLabel>
                                <StatValue>
                                    {summary.totalProfitPts != null ? `${summary.totalProfitPts > 0 ? '+' : ''}${summary.totalProfitPts}` : 'N/A'}
                                </StatValue>
                                <StatSubtext>Average: {summary.avgProfitPts || 0} pts</StatSubtext>
                            </StatItem>

                            <StatItem>
                                <StatLabel>Current Streak</StatLabel>
                                <StatValue $color={(summary.currentStreak || 0) >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                    {summary.currentStreak != null ? `${summary.currentStreak > 0 ? `+${summary.currentStreak} Wins` : `${Math.abs(summary.currentStreak)} Loss`}` : 'N/A'}
                                </StatValue>
                                <StatSubtext>Current win/loss streak</StatSubtext>
                            </StatItem>
                        </StatsGrid>
                    </Card>
                </MainGrid>

                {/* Global Seasonality Breakdown: Weekday & Month */}
                <SecondaryGrid>
                    {/* Average return by weekday (%) */}
                    <Card>
                        <CardHeader>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <CategoryTitle style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                                    AVERAGE RETURN BY WEEKDAY (%)
                                    <KeyIndicator title="Timing Signal: Best trading days of the week for ST (Short-Term) execution">★</KeyIndicator>
                                </CategoryTitle>
                                {!todayInfo.isWeekend && (
                                    <span style={{
                                        fontSize: '11px',
                                        color: currentWeekdayStat?.isGain ? '#80cbc4' : '#ef5350',
                                        background: currentWeekdayStat?.isGain ? 'rgba(128, 203, 196, 0.12)' : 'rgba(239, 83, 80, 0.12)',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        border: `1px solid ${currentWeekdayStat?.isGain ? 'rgba(128, 203, 196, 0.3)' : 'rgba(239, 83, 80, 0.3)'}`,
                                        fontWeight: 600
                                    }}>
                                        Today ({todayInfo.dayOfWeek}): {currentWeekdayStat ? (currentWeekdayStat.returnPct > 0 ? `+${currentWeekdayStat.returnPct}%` : `${currentWeekdayStat.returnPct}%`) : '--'}
                                    </span>
                                )}
                            </div>
                            <span style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400] }}>
                                {years}Y Day-of-Week Average
                            </span>
                        </CardHeader>
                        <div style={{ width: '100%', height: '220px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data?.weekdayReturns || []}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                >
                                    <XAxis
                                        dataKey="day"
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                        domain={['auto', 'auto']}
                                    />
                                    <ReferenceLine y={0} stroke="rgba(255, 255, 255, 0.2)" />
                                    <Tooltip
                                        cursor={{
                                             fill: theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.12)' : 'rgba(15, 118, 110, 0.08)',
                                             radius: 4
                                        }}
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const d = payload[0].payload;
                                                const isToday = d.day === todayInfo.dayOfWeek;
                                                return (
                                                    <CustomTooltipBox theme={theme}>
                                                        <div style={{ color: tokens(theme.palette.mode).grey[400], marginBottom: 4, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span>{d.day}</span>
                                                            {isToday && (
                                                                <span style={{ color: '#ffd54f', fontSize: '10px', fontWeight: 600 }}>• Today</span>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            color: d.isGain ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500],
                                                            fontWeight: 600
                                                        }}>
                                                            Avg Return: {d.returnPct > 0 ? `+${d.returnPct}%` : `${d.returnPct}%`}
                                                        </div>
                                                    </CustomTooltipBox>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="returnPct" radius={[2, 2, 0, 0]}>
                                        {(data?.weekdayReturns || []).map((entry, idx) => {
                                            const isToday = todayInfo.dayOfWeek === entry.day;
                                            return (
                                                <Cell
                                                    key={`cell-wd-${idx}`}
                                                    fill={entry.isGain
                                                        ? (isToday ? '#26a69a' : 'rgba(38, 166, 154, 0.7)')
                                                        : (isToday ? '#ef5350' : 'rgba(239, 83, 80, 0.7)')}
                                                    stroke={isToday ? '#ffd54f' : 'transparent'}
                                                    strokeWidth={isToday ? 2.5 : 0}
                                                />
                                            );
                                        })}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    {/* Average return by month (%) */}
                    <Card>
                        <CardHeader>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <CategoryTitle style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                                    AVERAGE RETURN BY MONTH (%)
                                    <KeyIndicator title="Timing Signal: Monthly seasonal bias for MT/LT swing trades">★</KeyIndicator>
                                </CategoryTitle>
                                <span style={{
                                    fontSize: '11px',
                                    color: currentMonthStat?.isGain ? '#80cbc4' : '#ef5350',
                                    background: currentMonthStat?.isGain ? 'rgba(128, 203, 196, 0.12)' : 'rgba(239, 83, 80, 0.12)',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    border: `1px solid ${currentMonthStat?.isGain ? 'rgba(128, 203, 196, 0.3)' : 'rgba(239, 83, 80, 0.3)'}`,
                                    fontWeight: 600
                                }}>
                                    Current ({todayInfo.monthLong}): {currentMonthStat ? (currentMonthStat.returnPct > 0 ? `+${currentMonthStat.returnPct}%` : `${currentMonthStat.returnPct}%`) : '--'}
                                </span>
                            </div>
                            <span style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400] }}>
                                {years}Y Calendar Month Average
                            </span>
                        </CardHeader>
                        <div style={{ width: '100%', height: '220px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data?.monthlyReturns || []}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                >
                                    <XAxis
                                        dataKey="month"
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                        domain={['auto', 'auto']}
                                    />
                                    <ReferenceLine y={0} stroke="rgba(255, 255, 255, 0.2)" />
                                    <Tooltip
                                        cursor={{
                                             fill: theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.12)' : 'rgba(15, 118, 110, 0.08)',
                                             radius: 4
                                        }}
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const d = payload[0].payload;
                                                const isCurrentMonth = d.month === todayInfo.monthShort;
                                                return (
                                                    <CustomTooltipBox theme={theme}>
                                                        <div style={{ color: tokens(theme.palette.mode).grey[400], marginBottom: 4, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span>{d.month}</span>
                                                            {isCurrentMonth && (
                                                                <span style={{ color: '#ffd54f', fontSize: '10px', fontWeight: 600 }}>• Current Month</span>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            color: d.isGain ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500],
                                                            fontWeight: 600
                                                        }}>
                                                            Avg Return: {d.returnPct > 0 ? `+${d.returnPct}%` : `${d.returnPct}%`}
                                                        </div>
                                                    </CustomTooltipBox>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="returnPct" radius={[2, 2, 0, 0]}>
                                        {(data?.monthlyReturns || []).map((entry, idx) => {
                                            const isCurrentMonth = todayInfo.monthShort === entry.month;
                                            return (
                                                <Cell
                                                    key={`cell-m-${idx}`}
                                                    fill={entry.isGain
                                                        ? (isCurrentMonth ? '#26a69a' : 'rgba(38, 166, 154, 0.7)')
                                                        : (isCurrentMonth ? '#ef5350' : 'rgba(239, 83, 80, 0.7)')}
                                                    stroke={isCurrentMonth ? '#ffd54f' : 'transparent'}
                                                    strokeWidth={isCurrentMonth ? 2.5 : 0}
                                                />
                                            );
                                        })}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </SecondaryGrid>

                {/* Secondary Row: Cumulative Profit & Individual Returns Bar Chart (Collapsible) */}
                <Card style={{ marginBottom: '16px', padding: 0, overflow: 'hidden' }}>
                    <CardHeader
                        onClick={() => setShowPerformanceCharts(prev => !prev)}
                        style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            padding: '12px 16px',
                            marginBottom: 0,
                            borderBottom: showPerformanceCharts ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'}` : 'none'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CategoryTitle style={{ margin: 0 }}>
                                CUMULATIVE PROFIT & INDIVIDUAL RETURNS
                            </CategoryTitle>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowPerformanceCharts(prev => !prev);
                            }}
                            title={showPerformanceCharts ? "Hide Details" : "Show Details"}
                            style={{
                                background: 'transparent',
                                border: `1px solid ${showPerformanceCharts ? (theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.4)' : 'rgba(15, 118, 110, 0.3)') : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)')}`,
                                borderRadius: '4px',
                                width: '26px',
                                height: '26px',
                                padding: 0,
                                cursor: 'pointer',
                                color: showPerformanceCharts ? (theme.palette.mode === 'dark' ? '#80cbc4' : '#0f766e') : tokens(theme.palette.mode).grey[400],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.18s ease'
                            }}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    transform: showPerformanceCharts ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                }}
                            >
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </button>
                    </CardHeader>

                    {showPerformanceCharts && (
                        <div style={{ padding: '16px' }}>
                            <SecondaryGrid style={{ marginBottom: 0 }}>
                                {/* Cumulative Profit Chart */}
                                <Card style={{ background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(15, 23, 42, 0.02)' }}>
                                    <CardHeader>
                                        <CategoryTitle style={{ margin: 0 }}>CUMULATIVE PROFIT (BASE 100)</CategoryTitle>
                                        <span style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400] }}>
                                            Compounded Historical Equity
                                        </span>
                                    </CardHeader>
                                    <div style={{ width: '100%', height: '220px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                data={data?.cumulativeProfit || []}
                                                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                            >
                                                <XAxis
                                                    dataKey="year"
                                                    stroke="rgba(255, 255, 255, 0.2)"
                                                    tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    stroke="rgba(255, 255, 255, 0.2)"
                                                    tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                                    tickLine={false}
                                                    domain={['auto', 'auto']}
                                                />
                                                <ReferenceLine y={100} stroke="rgba(255, 255, 255, 0.15)" strokeDasharray="3 3" />
                                                <Tooltip
                                                    cursor={{ stroke: 'rgba(128, 203, 196, 0.35)', strokeDasharray: '3 3' }}
                                                    content={({ active, payload }) => {
                                                        if (active && payload && payload.length) {
                                                            const d = payload[0].payload;
                                                            return (
                                                                <CustomTooltipBox theme={theme}>
                                                                    <div style={{ color: tokens(theme.palette.mode).grey[400], marginBottom: 4 }}>
                                                                        Year {d.year}
                                                                    </div>
                                                                    <div style={{ fontWeight: 600, color: '#e6edf7' }}>
                                                                        Equity: {d.value}
                                                                    </div>
                                                                    <div style={{
                                                                        color: d.returnPct >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500],
                                                                        fontWeight: 500
                                                                    }}>
                                                                        Trade: {d.returnPct > 0 ? `+${d.returnPct}%` : `${d.returnPct}%`}
                                                                    </div>
                                                                </CustomTooltipBox>
                                                            );
                                                        }
                                                        return null;
                                                    }}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke="#80cbc4"
                                                    strokeWidth={1.8}
                                                    dot={{ r: 3, fill: '#80cbc4' }}
                                                    isAnimationActive={false}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>

                                {/* Individual Returns Bar Chart (Seasonax bar chart) */}
                                <Card style={{ background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(15, 23, 42, 0.02)' }}>
                                    <CardHeader>
                                        <CategoryTitle style={{ margin: 0 }}>INDIVIDUAL RETURNS (%)</CategoryTitle>
                                        <span style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400] }}>
                                            Year-by-Year Distribution
                                        </span>
                                    </CardHeader>
                                    <div style={{ width: '100%', height: '220px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={data?.individualReturns || []}
                                                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                            >
                                                <XAxis
                                                    dataKey="year"
                                                    stroke="rgba(255, 255, 255, 0.2)"
                                                    tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    stroke="rgba(255, 255, 255, 0.2)"
                                                    tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                                    tickLine={false}
                                                    domain={['auto', 'auto']}
                                                />
                                                <ReferenceLine y={0} stroke="rgba(255, 255, 255, 0.2)" />
                                                <Tooltip
                                                    cursor={{
                                                        fill: theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.12)' : 'rgba(15, 118, 110, 0.08)',
                                                        radius: 4
                                                    }}
                                                    content={({ active, payload }) => {
                                                        if (active && payload && payload.length) {
                                                            const d = payload[0].payload;
                                                            return (
                                                                <CustomTooltipBox theme={theme}>
                                                                    <div style={{ color: tokens(theme.palette.mode).grey[400], marginBottom: 4 }}>
                                                                        Year {d.year}
                                                                    </div>
                                                                    <div style={{
                                                                        color: d.isGain ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500],
                                                                        fontWeight: 600
                                                                    }}>
                                                                        Return: {d.returnPct > 0 ? `+${d.returnPct}%` : `${d.returnPct}%`}
                                                                    </div>
                                                                </CustomTooltipBox>
                                                            );
                                                        }
                                                        return null;
                                                    }}
                                                />
                                                <Bar dataKey="returnPct" radius={[2, 2, 0, 0]}>
                                                    {(data?.individualReturns || []).map((entry, idx) => (
                                                        <Cell
                                                            key={`cell-${idx}`}
                                                            fill={entry.isGain
                                                                ? 'rgba(38, 166, 154, 0.8)'
                                                                : 'rgba(239, 83, 80, 0.8)'}
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>
                            </SecondaryGrid>
                        </div>
                    )}
                </Card>

                {/* Yearly Trades Breakdown Table (Collapsible) */}
                <Card style={{ marginBottom: '16px', padding: 0, overflow: 'hidden' }}>
                    <CardHeader
                        onClick={() => setShowTradeBreakdown(prev => !prev)}
                        style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            padding: '12px 16px',
                            marginBottom: 0,
                            borderBottom: showTradeBreakdown ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'}` : 'none'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <CategoryTitle style={{ margin: 0 }}>HISTORICAL TRADES BREAKDOWN</CategoryTitle>
                            <span style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400] }}>
                                ({data?.trades?.length || 0} trades)
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowTradeBreakdown(prev => !prev);
                            }}
                            title={showTradeBreakdown ? "Hide Details" : "Show Details"}
                            style={{
                                background: 'transparent',
                                border: `1px solid ${showTradeBreakdown ? (theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.4)' : 'rgba(15, 118, 110, 0.3)') : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)')}`,
                                borderRadius: '4px',
                                width: '26px',
                                height: '26px',
                                padding: 0,
                                cursor: 'pointer',
                                color: showTradeBreakdown ? (theme.palette.mode === 'dark' ? '#80cbc4' : '#0f766e') : tokens(theme.palette.mode).grey[400],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.18s ease'
                            }}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    transform: showTradeBreakdown ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                }}
                            >
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </button>
                    </CardHeader>

                    {showTradeBreakdown && (
                        <TableWrapper style={{ borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'}`, border: 'none', borderRadius: 0 }}>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>YEAR</Th>
                                        <Th>ENTRY DATE</Th>
                                        <Th>ENTRY PRICE</Th>
                                        <Th>EXIT DATE</Th>
                                        <Th>EXIT PRICE</Th>
                                        <Th>RETURN (%)</Th>
                                        <Th>PROFIT (PTS)</Th>
                                        <Th>MAX RISE (%)</Th>
                                        <Th>MAX DROP (%)</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.trades || []).map(trade => {
                                        const isPos = trade.returnPct > 0;
                                        const isNeg = trade.returnPct < 0;
                                        const retColor = isPos
                                            ? tokens(theme.palette.mode).greenAccent[500]
                                            : (isNeg ? tokens(theme.palette.mode).redAccent[500] : tokens(theme.palette.mode).grey[300]);

                                        return (
                                            <tr key={trade.year}>
                                                <Td>{trade.year}</Td>
                                                <Td $color={tokens(theme.palette.mode).grey[400]}>{trade.startDate}</Td>
                                                <Td>{trade.startPrice?.toLocaleString()}</Td>
                                                <Td $color={tokens(theme.palette.mode).grey[400]}>{trade.endDate}</Td>
                                                <Td>{trade.endPrice?.toLocaleString()}</Td>
                                                <Td $color={retColor}>
                                                    {trade.returnPct > 0 ? `+${trade.returnPct}%` : `${trade.returnPct}%`}
                                                </Td>
                                                <Td $color={retColor}>
                                                    {trade.profitPts > 0 ? `+${trade.profitPts}` : `${trade.profitPts}`}
                                                </Td>
                                                <Td $color={tokens(theme.palette.mode).greenAccent[500]}>
                                                    +{trade.maxRise}%
                                                </Td>
                                                <Td $color={trade.maxDrop < 0 ? tokens(theme.palette.mode).redAccent[500] : tokens(theme.palette.mode).grey[400]}>
                                                    {trade.maxDrop}%
                                                </Td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </TableWrapper>
                    )}
                </Card>
            </ScrollableContent>

            {/* Fullscreen Enlarged Chart Modal */}
            {isChartModalOpen && (
                <ModalOverlay onClick={() => setIsChartModalOpen(false)}>
                    <ExpandedModalContent onClick={(e) => e.stopPropagation()}>
                        <ExpandedModalHeader>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <CategoryTitle style={{ margin: 0, fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                                    ANNUAL SEASONAL TREND (BASE 100)
                                    <KeyIndicator title="Core Signal: Check if current seasonal trend direction aligns with Market Overview medal">★</KeyIndicator>
                                </CategoryTitle>
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#0f172a',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)',
                                    padding: '3px 10px',
                                    borderRadius: '4px'
                                }}>
                                    {data?.name} ({data?.asset})
                                </span>
                                {activeWindow?.label && (
                                    <span style={{
                                        fontSize: '11px',
                                        color: '#80cbc4',
                                        background: 'rgba(128, 203, 196, 0.08)',
                                        padding: '3px 10px',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(128, 203, 196, 0.25)',
                                        fontWeight: 600
                                    }}>
                                        Window: {activeWindow.label}
                                    </span>
                                )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                {/* Quick Presets inside modal */}
                                <PresetButtonGroup>
                                    <PresetBtn
                                        $active={activePreset === "THIS_MONTH"}
                                        onClick={() => handleApplyPreset("THIS_MONTH")}
                                    >
                                        This Month
                                    </PresetBtn>
                                    <PresetBtn
                                        $active={activePreset === "NEXT_14_DAYS"}
                                        onClick={() => handleApplyPreset("NEXT_14_DAYS")}
                                    >
                                        Next 14D
                                    </PresetBtn>
                                    <PresetBtn
                                        $active={activePreset === "BEST_PATTERN"}
                                        onClick={() => handleApplyPreset("BEST_PATTERN")}
                                    >
                                        Best Pattern
                                    </PresetBtn>
                                </PresetButtonGroup>

                                {/* Years Toggle */}
                                <ToggleGroup>
                                    {YEARS_OPTIONS.map(yr => (
                                        <ToggleBtn
                                            key={yr}
                                            active={years === yr}
                                            onClick={() => setYears(yr)}
                                            style={{ padding: '4px 8px', fontSize: '11px' }}
                                        >
                                            {yr}Y
                                        </ToggleBtn>
                                    ))}
                                </ToggleGroup>

                                {/* Close Button */}
                                <CloseModalBtn onClick={() => setIsChartModalOpen(false)} title="Close (Esc)">
                                    ✕
                                </CloseModalBtn>
                            </div>
                        </ExpandedModalHeader>

                        {/* Enlarged Chart Area */}
                        <div style={{ flex: 1, minHeight: 0, width: '100%', marginTop: '16px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={data?.seasonalCurve || []}
                                    margin={{ top: 15, right: 20, left: -10, bottom: 10 }}
                                    onMouseMove={(e) => {
                                        if (e && e.activePayload && e.activePayload.length) {
                                            setHoveredCurveDate(e.activePayload[0].payload.date);
                                        }
                                    }}
                                    onMouseLeave={() => setHoveredCurveDate(null)}
                                >
                                    <XAxis
                                        dataKey="date"
                                        ticks={MONTH_TICKS}
                                        tickFormatter={val => MONTH_LABELS[val] || val}
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[300], fontSize: 12, fontWeight: 500 }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[300], fontSize: 12 }}
                                        tickLine={false}
                                        domain={['auto', 'auto']}
                                    />
                                    <ReferenceLine y={100} stroke="rgba(255, 255, 255, 0.2)" strokeDasharray="4 4" />

                                    {/* Today Vertical Marker - label appears only when mouse approaches the line */}
                                    <ReferenceLine
                                        x={todayInfo.dateKey}
                                        stroke={isNearToday ? "#ffd54f" : "rgba(255, 213, 79, 0.45)"}
                                        strokeWidth={isNearToday ? 2.2 : 1.4}
                                        strokeDasharray="3 3"
                                        label={isNearToday ? {
                                            value: `Today (${todayInfo.label})`,
                                            position: 'insideTopRight',
                                            fill: '#ffd54f',
                                            fontSize: 11,
                                            fontWeight: 700
                                        } : undefined}
                                    />

                                    {/* Shaded Pattern Region */}
                                    {activeWindow?.start && activeWindow?.end && (
                                        isCrossYear ? (
                                            <>
                                                <ReferenceArea
                                                    x1={activeWindow.start}
                                                    x2="12-31"
                                                    fill="rgba(128, 203, 196, 0.15)"
                                                    stroke="rgba(128, 203, 196, 0.4)"
                                                    strokeDasharray="2 2"
                                                />
                                                <ReferenceArea
                                                    x1="01-01"
                                                    x2={activeWindow.end}
                                                    fill="rgba(128, 203, 196, 0.15)"
                                                    stroke="rgba(128, 203, 196, 0.4)"
                                                    strokeDasharray="2 2"
                                                />
                                            </>
                                        ) : (
                                            <ReferenceArea
                                                x1={activeWindow.start}
                                                x2={activeWindow.end}
                                                fill="rgba(128, 203, 196, 0.15)"
                                                stroke="rgba(128, 203, 196, 0.4)"
                                                strokeDasharray="2 2"
                                            />
                                        )
                                    )}

                                    <Tooltip
                                        cursor={{ stroke: 'rgba(128, 203, 196, 0.45)', strokeDasharray: '3 3' }}
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const d = payload[0].payload;
                                                const val = d.value;
                                                const diff = (val - 100).toFixed(2);
                                                return (
                                                    <CustomTooltipBox theme={theme} style={{ padding: '10px 14px', fontSize: '12px' }}>
                                                        <div style={{ color: tokens(theme.palette.mode).grey[300], marginBottom: 6, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span>{d.label}</span>
                                                            {d.date === todayInfo.dateKey && (
                                                                <span style={{
                                                                    color: '#ffd54f',
                                                                    fontSize: '11px',
                                                                    fontWeight: 700,
                                                                    background: 'rgba(255, 213, 79, 0.15)',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid rgba(255, 213, 79, 0.4)'
                                                                }}>
                                                                    • Today
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            color: val >= 100 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500],
                                                            fontWeight: 700,
                                                            fontSize: '13px'
                                                        }}>
                                                            Cumulative Trend: {val} ({diff >= 0 ? `+${diff}%` : `${diff}%`})
                                                        </div>
                                                    </CustomTooltipBox>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke={theme.palette.mode === 'dark' ? '#80cbc4' : '#0f766e'}
                                        strokeWidth={2.5}
                                        dot={false}
                                        activeDot={{ r: 5, fill: '#80cbc4', stroke: '#ffffff', strokeWidth: 1.5 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Quick Metrics Bar at bottom of expanded modal */}
                        {summary && summary.totalTrades > 0 && (
                            <ExpandedFooterStats theme={theme}>
                                <FooterStatItem>
                                    <FooterStatLabel>Win Rate</FooterStatLabel>
                                    <FooterStatVal $color={summary.winRate >= 50 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                        {summary.winRate}%
                                    </FooterStatVal>
                                </FooterStatItem>
                                <FooterStatItem>
                                    <FooterStatLabel>Avg Return</FooterStatLabel>
                                    <FooterStatVal $color={summary.avgReturn >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                        {summary.avgReturn > 0 ? `+${summary.avgReturn}%` : `${summary.avgReturn}%`}
                                    </FooterStatVal>
                                </FooterStatItem>
                                <FooterStatItem>
                                    <FooterStatLabel>Total Trades</FooterStatLabel>
                                    <FooterStatVal>{summary.totalTrades} ({summary.gainsCount}W / {summary.lossesCount}L)</FooterStatVal>
                                </FooterStatItem>
                                <FooterStatItem>
                                    <FooterStatLabel>Max Gain / Loss</FooterStatLabel>
                                    <FooterStatVal>
                                        <span style={{ color: tokens(theme.palette.mode).greenAccent[500] }}>+{summary.maxGain}%</span> / <span style={{ color: tokens(theme.palette.mode).redAccent[500] }}>{summary.maxLoss}%</span>
                                    </FooterStatVal>
                                </FooterStatItem>
                                <FooterStatItem>
                                    <FooterStatLabel>Avg Trading Days</FooterStatLabel>
                                    <FooterStatVal>{summary.tradingDays} days</FooterStatVal>
                                </FooterStatItem>
                            </ExpandedFooterStats>
                        )}
                    </ExpandedModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

export default SeasonalityTab;
